import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import MainLogo from "../../assets/images/AstraLogo.svg";

const TwoFactorVerification = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const initialValues = {
        otp1: "",
        otp2: "",
        otp3: "",
        otp4: "",
        otp5: "",
        otp6: "",
    };

    const validationSchema = Yup.object()
        .shape({
            otp1: Yup.string(),
            otp2: Yup.string(),
            otp3: Yup.string(),
            otp4: Yup.string(),
            otp5: Yup.string(),
            otp6: Yup.string(),
        })
        .test("allFilled", "All verification code fields are required", function (value) {
            return Object.values(value).every((otp) => otp.trim() !== "");
        });

    const handleInputChange = (e, setFieldValue, values) => {
        const { maxLength, value, name } = e.target;
        const [fieldIndex] = name.split("otp");
        if (value.length >= maxLength) {
            if (fieldIndex < 6) {
                const nextField = document.querySelector(
                    `input[name=otp${parseInt(fieldIndex) + 1}]`
                );
                if (nextField !== null) {
                    nextField.focus();
                }
            }
        }
        setFieldValue(name, value);
        if (value.length === 6 && fieldIndex === "1") {
            value.split("").forEach((digit, idx) => {
                setFieldValue(`otp${idx + 1}`, digit);
            });
        }
    };

    const handleKeyDown = (e, setFieldValue, values) => {
        const { name, value } = e.target;
        const [fieldIndex] = name.split("otp");
        if (e.key === "Backspace" && !value) {
            if (fieldIndex > 1) {
                const prevField = document.querySelector(
                    `input[name=otp${parseInt(fieldIndex) - 1}]`
                );
                if (prevField !== null) {
                    prevField.focus();
                    setFieldValue(`otp${parseInt(fieldIndex) - 1}`, "");
                }
            }
        }
    };

    const handlePaste = (e, setFieldValue) => {
        const paste = e.clipboardData.getData("text");
        if (paste.length === 6) {
            paste.split("").forEach((char, index) => {
                setFieldValue(`otp${index + 1}`, char);
            });
            document.querySelector(`input[name=otp6]`).focus();
        }
        e.preventDefault();
    };

    const handleSubmit = async (values) => {

        try {
            // Replace with your API call
            //   const response = await verifyAuthenticatorCode({ otp });
            const response = 200;
            if (response.status === 200) {
                enqueueSnackbar("Verification successful", {
                    variant: "success",
                    anchorOrigin: { horizontal: "right", vertical: "top" },
                });
                navigate("/dashboard");
            } else {
                enqueueSnackbar(response.data.error.message, {
                    variant: "error",
                    anchorOrigin: { horizontal: "right", vertical: "top" },
                });
            }
        } catch (error) {
            enqueueSnackbar("An error occurred. Please try again.", {
                variant: "error",
                anchorOrigin: { horizontal: "right", vertical: "top" },
            });
        }
    };

    return (
        <div className="login-signup-wrap forget-wrap">
            <div className="container">
                <div className="row">
                    <div className="col-md-12 col-lg-6 logo-wrap place-content-center">
                        <Link>
                            <img className="login-signup-logo" src={MainLogo} alt="Main Logo" />
                        </Link>
                        <div className="auth-page-content">
                            <h2>
                                Verify Your Account
                            </h2>
                            <p>
                                Enter the 6-digit code from your authenticator app. Confirm your identity and continue to your financial dashboard.
                            </p>
                            <p className="w-100">
                                <svg width="46" height="3" viewBox="0 0 46 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 1.5H46" stroke="white" strokeWidth="2" />
                                </svg>
                                &nbsp;&nbsp;&nbsp;
                                PROFESSIONAL FINANCIAL ADVISORY</p>
                        </div>
                    </div>
                    <div className="col-md-12 col-lg-6">
                        <div className="login-signup-box signup-box">
                            <h2 className="login-signup-head text-center">Two-Factor Authentication</h2>
                            <div style={{ textAlign: "center", marginBottom: "25px" }}>
                                <svg width="148" height="5" viewBox="0 0 148 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 3.5C2.46003 3.55832 4.91997 3.61164 7.37983 3.65996C29.5186 4.09484 51.6504 4.12478 73.7754 3.74978C95.9004 3.37478 118.019 2.59484 140.13 1.40996C142.587 1.27831 145.043 1.14165 147.5 1C145.04 0.941681 142.58 0.888362 140.12 0.840041C117.981 0.405158 95.8496 0.375215 73.7246 0.750215C51.5996 1.12522 29.4814 1.90516 7.37017 3.09004C4.91336 3.22169 2.45664 3.35835 0 3.5Z" fill="url(#paint0_linear_1_223)" />
                                    <defs>
                                        <linearGradient id="paint0_linear_1_223" x1="45" y1="-7" x2="107.5" y2="9" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#D9D9D9" />
                                            <stop offset="1" stopColor="#DBDBDB" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ isSubmitting, setFieldValue, values }) => (
                                    <Form>
                                        <div className="form-group">
                                            <label htmlFor="otp">
                                                Enter the 6-digit code from your authenticator app.
                                            </label>
                                            <div className="d-flex otp-box">
                                                {[
                                                    "otp1",
                                                    "otp2",
                                                    "otp3",
                                                    "otp4",
                                                    "otp5",
                                                    "otp6",
                                                ].map((otp) => (
                                                    <Field
                                                        key={otp}
                                                        type="text"
                                                        name={otp}
                                                        maxLength="1"
                                                        className="form-control"
                                                        onChange={(e) =>
                                                            handleInputChange(e, setFieldValue, values)
                                                        }
                                                        onPaste={(e) => handlePaste(e, setFieldValue)}
                                                        onKeyDown={(e) =>
                                                            handleKeyDown(e, setFieldValue, values)
                                                        }
                                                    />
                                                ))}
                                            </div>
                                            {[
                                                "otp1",
                                                "otp2",
                                                "otp3",
                                                "otp4",
                                                "otp5",
                                                "otp6",
                                            ].map((otp) => (
                                                <ErrorMessage
                                                    key={otp}
                                                    name={otp}
                                                    component="div"
                                                    className="text-danger"
                                                />
                                            ))}
                                        </div>
                                        <button
                                            type="submit"
                                            className="as-btn-primary animated-btn w-100 mt-2 mb-2"
                                            disabled={isSubmitting}
                                        >
                                            Verify &nbsp;&nbsp;
                                            <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14 5H1M14 5L10 9M14 5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                        <p className="text-center text-secondary mt-3 mobile-hidden-text f-700">
                                            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M16.5 7L2.25 7" stroke="#212121" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                                                <path d="M7.5 1L1.5 7L7.5 13" stroke="#212121" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                                            </svg>
                                            &nbsp;&nbsp;
                                            <Link className="text-dark" to="/login">
                                                Back to login
                                            </Link>
                                        </p>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorVerification;